/**
 * v-tooltip — collision-aware singleton tooltip directive.
 *
 * Renders one shared <div> in <body> (lazy, on first use) and points it at
 * the hovered/focused element via `@floating-ui/dom`'s `computePosition`.
 * Listeners attach to the directive's host element only; the singleton has
 * no per-instance reactive overhead, so a page with hundreds of tooltips
 * still costs ~one position calc per hover.
 *
 *   <button v-tooltip="'Save'">⌘S</button>
 *   <button v-tooltip="{ text: 'Save', placement: 'right' }">⌘S</button>
 *
 * Why a singleton portaled to <body>:
 *   - Tooltips rendered as ::before / ::after on the host are clipped by
 *     any overflow:hidden ancestor and the viewport edge, with no flip
 *     fallback. v-tooltip lives outside every clipping ancestor and uses
 *     floating-ui's flip/shift to stay on-screen.
 *
 * Why this and not `useFloating` (`@floating-ui/vue`):
 *   - useFloating creates per-instance reactive refs + autoUpdate listeners
 *     for the lifetime of the component. Fine for interactive popovers
 *     (menus, hover cards with Vue content), but wasteful for transient
 *     hover labels. v-tooltip pays nothing while idle.
 *
 * When NOT to use v-tooltip:
 *   - The floating panel needs Vue components / slots (not a plain string).
 *   - The user must be able to mouse from anchor → panel (hover bridge),
 *     click links inside, or keep it open via reactive state.
 *   - Multiple panels need to coexist on screen.
 *   Reach for `useFloating` from `@floating-ui/vue` for those cases.
 */

import type { Placement } from "@floating-ui/dom"
import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom"
import type { Directive } from "vue"

type TooltipBinding =
  | string
  | null
  | undefined
  | {
      text?: string
      placement?: Placement
      hideDelay?: number
      /** Extra CSS class applied to the singleton tip element while this
       *  host is active. Removed on hide. Use to opt into per-tooltip
       *  styling (wider max-width, mono font, etc.) without overriding
       *  the singleton globally. */
      class?: string
    }

interface TooltipState {
  text: string
  placement: Placement
  /** Delay in ms before hiding on pointerleave/blur. Cancelled by a
   *  re-entry — prevents flicker on tiny targets where the cursor can
   *  briefly cross a sub-pixel gap. Defaults to 0 (immediate hide). */
  hideDelay: number
  extraClass: string
  onEnter: () => void
  onLeave: () => void
  onFocus: () => void
  onBlur: () => void
}

const HOSTS = new WeakMap<HTMLElement, TooltipState>()

let tipEl: HTMLElement | null = null
let activeHost: HTMLElement | null = null
let cleanupAutoUpdate: (() => void) | null = null
let hideTimer: ReturnType<typeof setTimeout> | null = null
let activeExtraClass = ""

function clearHideTimer() {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

function ensureTooltipElement(): HTMLElement {
  if (tipEl) return tipEl
  const el = document.createElement("div")
  el.className = "v-tooltip-floating"
  // role=tooltip lets AT announce the contents when the host has
  // aria-describedby; we don't manage that pairing here because hover-only
  // tooltips on truly essential information are an a11y antipattern anyway
  // — surface the text in the trigger label too.
  el.setAttribute("role", "tooltip")
  el.style.position = "absolute"
  el.style.top = "0"
  el.style.left = "0"
  el.style.pointerEvents = "none"
  el.style.opacity = "0"
  el.style.visibility = "hidden"
  // popover="manual" (+ a one-time showPopover() below) promotes this
  // element into the browser's top layer, same as a native <dialog>. A
  // regular <body>-appended div renders BELOW any open <dialog> no matter
  // its z-index; the top layer is the only way to sit above one. We open
  // the popover exactly once and never close it — all subsequent show/hide
  // is still driven entirely by the opacity/visibility toggling below, so
  // the existing fade transition is untouched. If the browser doesn't
  // support the Popover API, the attribute is simply ignored and this
  // degrades to the pre-existing (non-dialog-aware) behavior.
  if ("showPopover" in el) {
    el.setAttribute("popover", "manual")
  }
  document.body.appendChild(el)
  if (typeof el.showPopover === "function") {
    try {
      el.showPopover()
    } catch {
      // Ignore — e.g. already open, or an environment without real support
      // despite feature-detecting truthy. Falls back to normal stacking.
    }
  }
  tipEl = el
  return el
}

function parseBinding(value: TooltipBinding): {
  text: string
  placement: Placement
  hideDelay: number
  extraClass: string
} {
  if (typeof value === "string") {
    return { text: value, placement: "top", hideDelay: 0, extraClass: "" }
  }
  if (value && typeof value === "object") {
    return {
      text: value.text ?? "",
      placement: value.placement ?? "top",
      hideDelay: Math.max(0, value.hideDelay ?? 0),
      extraClass: value.class ?? "",
    }
  }
  return { text: "", placement: "top", hideDelay: 0, extraClass: "" }
}

function splitClasses(value: string): string[] {
  return value.split(/\s+/).filter(Boolean)
}

function hideNow() {
  if (!tipEl) return
  tipEl.style.opacity = "0"
  tipEl.style.visibility = "hidden"
  // NOTE: don't strip activeExtraClass here. The tooltip fades out via the
  // opacity transition (~150ms) so the geometry needs to keep matching the
  // previous host until it's actually offscreen, or the box visibly snaps
  // narrow mid-fade. Classes are swapped lazily on the next show().
  if (cleanupAutoUpdate) {
    cleanupAutoUpdate()
    cleanupAutoUpdate = null
  }
  activeHost = null
}

function scheduleHide(delay: number) {
  clearHideTimer()
  if (delay <= 0) {
    hideNow()
    return
  }
  hideTimer = setTimeout(() => {
    hideTimer = null
    hideNow()
  }, delay)
}

function show(host: HTMLElement, state: TooltipState) {
  if (!state.text) return
  clearHideTimer()
  const tip = ensureTooltipElement()
  tip.textContent = state.text
  // Swap any extra classes set by a previous host for this host's, so
  // the singleton's appearance matches whichever tooltip is active.
  // Space-separated lists are supported, so split before touching classList
  // (which silently fails on multi-class strings).
  if (activeExtraClass && activeExtraClass !== state.extraClass) {
    for (const cls of splitClasses(activeExtraClass)) tip.classList.remove(cls)
  }
  if (state.extraClass) {
    for (const cls of splitClasses(state.extraClass)) tip.classList.add(cls)
  }
  activeExtraClass = state.extraClass
  activeHost = host

  const update = () => {
    if (activeHost !== host) return
    computePosition(host, tip, {
      placement: state.placement,
      middleware: [offset(6), flip(), shift({ padding: 8 })],
    }).then(({ x, y }) => {
      if (activeHost !== host) return
      tip.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`
      tip.style.opacity = "1"
      tip.style.visibility = "visible"
    })
  }

  // autoUpdate keeps the tooltip glued to the host while the host scrolls
  // or resizes (e.g. inside a virtualized column). Cleanup runs on hide.
  cleanupAutoUpdate = autoUpdate(host, tip, update)
}

export const tooltip: Directive<HTMLElement, TooltipBinding> = {
  mounted(el, binding) {
    const { text, placement, hideDelay, extraClass } = parseBinding(binding.value)
    const state: TooltipState = {
      text,
      placement,
      hideDelay,
      extraClass,
      onEnter: () => show(el, state),
      onLeave: () => {
        if (activeHost === el) scheduleHide(state.hideDelay)
      },
      onFocus: () => show(el, state),
      onBlur: () => {
        if (activeHost === el) scheduleHide(state.hideDelay)
      },
    }
    HOSTS.set(el, state)
    el.addEventListener("pointerenter", state.onEnter)
    el.addEventListener("pointerleave", state.onLeave)
    el.addEventListener("focus", state.onFocus)
    el.addEventListener("blur", state.onBlur)
  },
  updated(el, binding) {
    const state = HOSTS.get(el)
    if (!state) return
    const { text, placement, hideDelay, extraClass } = parseBinding(binding.value)
    state.text = text
    state.placement = placement
    state.hideDelay = hideDelay
    state.extraClass = extraClass
    if (activeHost === el && tipEl) tipEl.textContent = text
  },
  unmounted(el) {
    const state = HOSTS.get(el)
    if (!state) return
    el.removeEventListener("pointerenter", state.onEnter)
    el.removeEventListener("pointerleave", state.onLeave)
    el.removeEventListener("focus", state.onFocus)
    el.removeEventListener("blur", state.onBlur)
    HOSTS.delete(el)
    if (activeHost === el) hideNow()
  },
}
